import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PC_MARK } from "@/lib/assets";
import { Seo } from "@/components/Seo";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3 pc-reveal">
        <img src={PC_MARK} alt="PropertyConnect" className="h-16 w-auto animate-pulse" />
        <p className="text-sm text-slate-500">Loading PropertyConnect…</p>
      </div>
    </div>
  );
}

export function ProtectedRoute({ children, adminOnly, proOnly, verifiedProOnly }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined) return <Splash />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  // Alpha email-verification gate: authenticated but unverified users are held on the
  // verification screen. Admins are always treated as verified by the backend.
  if (user.email_verified === false && user.role !== "ADMIN") return <Navigate to="/verify-required" replace />;
  if (adminOnly && user.role !== "ADMIN") return <Navigate to="/" replace />;
  const at = user.account_type || "PROFESSIONAL";
  const isConsumer = at === "CONSUMER";
  // Only PROFESSIONAL accounts (and admins) may access professional-only routes.
  // Referral Partners are NOT professionals — deny-by-default at the route layer too.
  const isPro = at === "PROFESSIONAL" || user.role === "ADMIN";
  if ((proOnly || verifiedProOnly) && !isPro) return <Navigate to="/" replace />;
  // Verified Professional Participation Gate: unverified/pending/rejected professionals are
  // routed to the Profile onboarding surface instead of operational professional routes.
  if (verifiedProOnly && !isConsumer && user.role !== "ADMIN"
      && user.verification_status !== "PROFESSIONAL_VERIFIED") {
    return <Navigate to="/profile" replace />;
  }
  // Private/admin/CRM/account surfaces are never search-indexable.
  return <><Seo robots="noindex,follow" />{children}</>;
}

export function PublicOnlyRoute({ children }) {
  const { user } = useAuth();
  // Decide target on the FIRST render where a user exists (i.e. after submit set the flag),
  // then cache it in a ref so StrictMode's double-render can't re-read after we clear the flag.
  const decided = React.useRef(null);
  if (user === undefined) return <Splash />;
  if (user) {
    if (decided.current === null) {
      const jr = sessionStorage.getItem("pc_just_registered");
      if (jr) sessionStorage.removeItem("pc_just_registered");
      decided.current = jr ? "/profile" : "/";
    }
    return <Navigate to={decided.current} replace />;
  }
  // Auth entry pages (login/register/forgot-password) are not search-indexable.
  return <><Seo robots="noindex,follow" />{children}</>;
}
